import React from 'react';
import { useLocation } from 'react-router-dom';

import { AdminListPageConfig, AdminListPageRenderer, TabDef, useAdminListPage } from 'hooks/useAdminListPage';
import { QuestionTemplate, QuestionTemplateType } from 'models/flexible/QuestionTemplate';
import { adminIndexClassifications } from 'services/api/admin/classifications';
import { adminIndexClients } from 'services/api/admin/clients';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import { adminIndexQuestionTemplates, adminQuestionTemplatesQueryKeys } from 'services/api/admin/questionTemplates';
import type { AdminQuestionTemplateIndexFields } from 'services/api/fields';
import PencilIcon from 'svg/PencilIcon';

// ---------------------------------------------------------------------------
// Shared filters
// ---------------------------------------------------------------------------

const sharedFilters = [
  {
    key: 'client',
    label: 'Client',
    type: 'dropdown' as const,
    fetchConfig: {
      queryKey: ['adminClients', 'questionTemplates', 'filter'],
      fetchFn: adminIndexClients,
      params: { sortBy: 'name asc' },
    },
    getOptionLabel: (o: any) => o.name,
    getOptionValue: (o: any) => o.id,
    toRequest: (value: any) => value?.id || undefined,
  },
  {
    key: 'groupType',
    label: 'Managed Care Options',
    type: 'dropdown' as const,
    fetchConfig: {
      queryKey: ['adminGroupTypes', 'provider', 'questionTemplates', 'filter'],
      fetchFn: indexGroupTypes,
      params: { type: 'provider', sortBy: 'displayName asc' },
    },
    getOptionLabel: (o: any) => o.displayName,
    getOptionValue: (o: any) => o.id,
    toRequest: (value: any) => value?.id || undefined,
  },
  {
    key: 'episode',
    label: 'Episode Type',
    type: 'dropdown' as const,
    fetchConfig: {
      queryKey: ['adminClassifications', 'episode', 'questionTemplates', 'filter'],
      fetchFn: adminIndexClassifications,
      params: { type: 'episode', sortBy: 'name asc' },
    },
    getOptionLabel: (o: any) => o.name,
    getOptionValue: (o: any) => o.id,
    toRequest: (value: any) => value?.id || undefined,
  },
  {
    key: 'planType',
    label: 'Plan Type',
    type: 'dropdown' as const,
    fetchConfig: {
      queryKey: ['adminClassifications', 'planType', 'questionTemplates', 'filter'],
      fetchFn: adminIndexClassifications,
      params: { type: 'plan_type', sortBy: 'name asc' },
    },
    getOptionLabel: (o: any) => o.name,
    getOptionValue: (o: any) => o.id,
    toRequest: (value: any) => value?.id || undefined,
  },
];

// ---------------------------------------------------------------------------
// Progress config
// ---------------------------------------------------------------------------

// QuestionTemplate<AdminQuestionTemplateIndexFields> = Override<QuestionTemplate, Resolve<IndexMap, never>>
//
// The index API returns a different shape than the show API for the same model.
// For example, groupTypes is a pre-joined string on the index endpoint but an
// array of objects on the show endpoint. Override<Model, IndexMap['_']> replaces
// the model's property types with what the index endpoint actually returns,
// while preserving the model's prototype (getters like isProgress, isDischarge).
// This lets column renderers like `row.groupTypes || 'All'` type-check correctly
// as string access instead of erroring on `.map()` for an array type.

const INCLUDES = [] as const;

const progressConfig: AdminListPageConfig<
  QuestionTemplate<AdminQuestionTemplateIndexFields<(typeof INCLUDES)[number]>>
> = {
  title: 'Progress Templates',
  columnStorageKey: 'admin.progressTemplates',
  queryKey: adminQuestionTemplatesQueryKeys.index({ type: QuestionTemplateType.PROGRESS }),
  fetchFn: (params, signal) =>
    adminIndexQuestionTemplates({ ...params, type: QuestionTemplateType.PROGRESS }, signal, [...INCLUDES]),
  includes: INCLUDES,
  fetchParams: { defaultSort: 'clientName asc' },
  showResultCount: true,

  addButton: {
    label: 'Add Template',
    path: '/admin/question-templates/progress/new',
    permission: 'adminQuestionTemplateCreate',
  },

  filters: sharedFilters,

  columns: [
    {
      id: 'clientName',
      header: 'Client',
      render: (row) => row.client ?? '',
    },
    {
      id: 'groupTypes',
      header: 'Managed Care Options',
      sortable: false,
      render: (row) => row.groupTypes || 'All',
    },
    {
      id: 'episodeClassifications',
      header: 'Episode Type',
      sortable: false,
      render: (row) => row.episodeClassifications || '—',
    },
    {
      id: 'planTypeClassifications',
      header: 'Plan Type',
      sortable: false,
      render: (row) => row.planTypeClassifications || '—',
    },
    {
      id: 'trackLos',
      header: 'Track Days?',
      render: (row) => (row.trackLos ? 'Yes' : 'No'),
    },
    {
      id: 'questionCount',
      header: 'Questions',
      render: (row) => String(row.questionCount),
    },
  ],

  rowActions: [
    {
      label: 'Edit',
      icon: <PencilIcon width={18} height={18} />,
      permission: 'adminQuestionTemplateView',
      onClick: (row, { navigate }) => navigate(`/admin/question-templates/progress/${row.id}/edit`),
    },
  ],
};

// ---------------------------------------------------------------------------
// Discharge config
// ---------------------------------------------------------------------------

const dischargeConfig: AdminListPageConfig<
  QuestionTemplate<AdminQuestionTemplateIndexFields<(typeof INCLUDES)[number]>>
> = {
  title: 'Discharge Templates',
  columnStorageKey: 'admin.dischargeTemplates',
  queryKey: adminQuestionTemplatesQueryKeys.index({ type: QuestionTemplateType.DISCHARGE }),
  fetchFn: (params, signal) =>
    adminIndexQuestionTemplates({ ...params, type: QuestionTemplateType.DISCHARGE }, signal, [...INCLUDES]),
  includes: INCLUDES,
  fetchParams: { defaultSort: 'clientName asc' },
  showResultCount: true,

  addButton: {
    label: 'Add Template',
    path: '/admin/question-templates/discharge/new',
    permission: 'adminQuestionTemplateCreate',
  },

  filters: sharedFilters,

  columns: [
    {
      id: 'clientName',
      header: 'Client',
      render: (row) => row.client ?? '',
    },
    {
      id: 'groupTypes',
      header: 'Managed Care Options',
      sortable: false,
      render: (row) => row.groupTypes || 'All',
    },
    {
      id: 'episodeClassifications',
      header: 'Episode Type',
      sortable: false,
      render: (row) => row.episodeClassifications || '—',
    },
    {
      id: 'planTypeClassifications',
      header: 'Plan Type',
      sortable: false,
      render: (row) => row.planTypeClassifications || '—',
    },
    {
      id: 'questionCount',
      header: 'Questions',
      render: (row) => String(row.questionCount),
    },
  ],

  rowActions: [
    {
      label: 'Edit',
      icon: <PencilIcon width={18} height={18} />,
      permission: 'adminQuestionTemplateView',
      onClick: (row, { navigate }) => navigate(`/admin/question-templates/discharge/${row.id}/edit`),
    },
  ],
};

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

const TABS: TabDef[] = [
  { label: 'Progress Templates', path: '/admin/question-templates/progress', permission: 'adminQuestionTemplateView' },
  {
    label: 'Discharge Templates',
    path: '/admin/question-templates/discharge',
    permission: 'adminQuestionTemplateView',
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function QuestionTemplates() {
  const { pathname } = useLocation();
  const isDischarge = pathname.includes('/discharge');
  const activeConfig = isDischarge ? dischargeConfig : progressConfig;
  const currentTab = isDischarge ? 'Discharge Templates' : 'Progress Templates';
  const state = useAdminListPage(activeConfig);

  return (
    <AdminListPageRenderer
      config={activeConfig}
      state={state}
      tabs={{ pageTitle: 'Question Templates', tabDefs: TABS, currentTabLabel: currentTab }}
    />
  );
}
